import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  BarChart3,
  Timer,
  Target,
  Building2,
  GraduationCap,
  Clipboard,
  FileSpreadsheet,
  PenLine,
  Cable,
  Check,
  Minus,
  Sparkles,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import MarketingPageLayout from "../components/MarketingPageLayout";
import SeoHelmet from "../components/SeoHelmet";
import ScrollAnimation from "../../landing-page/components/ScrollAnimation";
import { BeamButton } from "../../landing-page/components/BeamButton";
import { MARKETING_META } from "../seoMeta";
import { JOURNAL_DE_TRADING_FAQ } from "../data/journalDeTradingFaq";
import { FEATURE_SOFTWARE_LD } from "../data/featurePages";
import { buildFaqPageLd, buildBreadcrumbLd, SITE_ORIGIN } from "../../shared/seoJsonLd";
import useTranslation from "../../client/hooks/useTranslation";

const PATH = "/journal-de-trading";

// Cycled accent palette — mirrors the homepage FeaturesGrid / FeatureCardGrid.
const ACCENTS = ["#00C9A7", "#3D6EFF", "#A855F7", "#F59E0B", "#EC4899", "#34D399"];

// Icons for the six pillars, in copy order.
const PILLAR_ICONS: LucideIcon[] = [
  BookOpen,
  BarChart3,
  Timer,
  Target,
  Building2,
  GraduationCap,
];

// Icons for the four import methods, in copy order.
const IMPORT_ICONS: LucideIcon[] = [Clipboard, FileSpreadsheet, PenLine, Cable];

// Shared HUD grid + ambient glow backdrop used across sections.
function HudBackdrop({ opacity = 1 }: { opacity?: number }) {
  return (
    <>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          opacity,
          backgroundImage: `
            linear-gradient(rgba(0,201,167,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,201,167,0.03) 1px, transparent 1px)
          `,
          backgroundSize: "48px 48px",
        }}
      />
    </>
  );
}

// count-up animation on a numeric target, respects reduced-motion.
function useCountUp(ref: React.RefObject<HTMLElement | null>, target: number, suffix = "") {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      el.textContent = `${target}${suffix}`;
      return;
    }
    let rafId = 0;
    const io = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const dur = 1200;
      const tick = (now: number) => {
        const p = Math.min(1, (now - start) / dur);
        el.textContent = `${(target * p).toFixed(target % 1 ? 1 : 0)}${suffix}`;
        if (p < 1) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    });
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [target, suffix]);
}

export default function JournalDeTradingPage() {
  const t = useTranslation();
  const m = MARKETING_META[PATH];
  const c = t.marketing.journalPage;
  const winRef = useRef<HTMLSpanElement>(null);
  const pfRef = useRef<HTMLSpanElement>(null);
  const ddRef = useRef<HTMLSpanElement>(null);
  useCountUp(winRef, 58, "%");
  useCountUp(pfRef, 1.9);
  useCountUp(ddRef, 4.2, "%");

  const stats = [
    { ref: winRef, initial: "0%", label: c.statsWinRate },
    { ref: pfRef, initial: "0", label: c.statsProfitFactor },
    { ref: ddRef, initial: "0%", label: c.statsDrawdown },
  ];

  // "améliorez chaque trade" → highlight the last two words in teal without
  // touching the translated copy. Falls back gracefully if the string is short.
  const heroWords = c.heroTitle.split(" ");
  const heroHead = heroWords.slice(0, -2).join(" ");
  const heroTail = heroWords.slice(-2).join(" ");

  const faqLd = buildFaqPageLd(
    JOURNAL_DE_TRADING_FAQ.map((f) => ({ question: f.question, answer: f.answer })),
  );
  const breadcrumbLd = buildBreadcrumbLd([
    { name: "Accueil", url: `${SITE_ORIGIN}/` },
    { name: m.seoBody!.h1, url: `${SITE_ORIGIN}${PATH}` },
  ]);
  const jsonLd = [FEATURE_SOFTWARE_LD, faqLd, breadcrumbLd].filter(Boolean) as Record<string, any>[];

  return (
    <MarketingPageLayout>
      <SeoHelmet path={PATH} title={m.title} description={m.description} jsonLd={jsonLd} />

      {/* ─────────────────────────── HERO ─────────────────────────── */}
      <section className="relative mx-[calc(-50vw+50%)] w-screen overflow-hidden">
        <HudBackdrop />
        {/* Ambient glows */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 left-1/4 h-[500px] w-[600px] -translate-x-1/2 rounded-full bg-primary/[0.08] blur-[120px]" />
          <div className="absolute top-1/3 -right-40 h-[400px] w-[400px] rounded-full bg-secondary/[0.06] blur-[110px]" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-5 pt-28 pb-16 text-center sm:px-6 sm:pt-36 sm:pb-20 lg:px-8">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/[0.06] px-4 py-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-primary"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {t.marketing.nav.megaFeatures[0].label}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display text-[34px] font-extrabold leading-[1.08] tracking-tight text-foreground sm:text-5xl md:text-6xl"
            style={{ textWrap: "balance" }}
          >
            {heroHead}{" "}
            <span className="text-primary">{heroTail}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg"
          >
            {c.heroSubtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-10 flex justify-center"
          >
            <BeamButton size="lg" showArrow className="px-7 text-base">
              {c.heroCta}
            </BeamButton>
          </motion.div>

          {/* Stat cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="mx-auto mt-14 grid max-w-2xl grid-cols-3 gap-3 sm:gap-5"
          >
            {stats.map((s) => (
              <div
                key={s.label}
                className="group relative overflow-hidden rounded-xl border border-border/50 bg-card/50 px-3 py-5 backdrop-blur-sm sm:px-5 sm:py-6"
              >
                <div className="pointer-events-none absolute left-0 top-0 h-3.5 w-3.5 border-l border-t border-primary/40" />
                <div className="pointer-events-none absolute bottom-0 right-0 h-3.5 w-3.5 border-b border-r border-primary/40" />
                <span
                  ref={s.ref}
                  aria-hidden="true"
                  className="block font-mono text-2xl font-bold tabular-nums text-primary sm:text-3xl"
                >
                  {s.initial}
                </span>
                <small className="mt-1.5 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground sm:text-xs">
                  {s.label}
                </small>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ────────────────────────── PILLARS ────────────────────────── */}
      <section className="relative mx-[calc(-50vw+50%)] w-screen px-6 py-16 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <ScrollAnimation direction="up">
            <SectionHeading title={c.pillarsTitle} />
          </ScrollAnimation>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {c.pillars.map((p, i) => {
              const accent = ACCENTS[i % ACCENTS.length];
              const Icon = PILLAR_ICONS[i % PILLAR_ICONS.length];
              return (
                <ScrollAnimation key={p.title} direction="up" delay={i * 60}>
                  <article className="group relative flex h-full min-h-[180px] flex-col overflow-hidden rounded-xl border border-border/40 bg-card/50 backdrop-blur-sm transition-all duration-300 hover:border-border/80 hover:bg-card/80">
                    {/* HUD corners */}
                    <div
                      className="pointer-events-none absolute left-0 top-0 h-4 w-4 border-l border-t opacity-40 transition-opacity duration-300 group-hover:opacity-80"
                      style={{ borderColor: accent }}
                    />
                    <div
                      className="pointer-events-none absolute bottom-0 right-0 h-4 w-4 border-b border-r opacity-40 transition-opacity duration-300 group-hover:opacity-80"
                      style={{ borderColor: accent }}
                    />
                    {/* Ambient hover glow */}
                    <div
                      className="pointer-events-none absolute -right-16 -top-16 h-32 w-32 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100"
                      style={{ backgroundColor: `${accent}22` }}
                    />
                    {/* Top accent line */}
                    <div
                      className="absolute left-0 right-0 top-0 h-px opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                      style={{ background: `linear-gradient(90deg, transparent, ${accent}60, transparent)` }}
                    />

                    <div className="relative z-10 flex h-full flex-col p-6">
                      <div
                        className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg border transition-all duration-300 group-hover:scale-110"
                        style={{ backgroundColor: `${accent}14`, borderColor: `${accent}30` }}
                      >
                        <Icon className="h-5 w-5" style={{ color: accent }} />
                      </div>
                      <h3 className="mb-2 text-base font-semibold leading-snug text-foreground">
                        {p.title}
                      </h3>
                      <p className="flex-1 text-sm leading-relaxed text-muted-foreground">
                        {p.body}
                      </p>
                    </div>
                  </article>
                </ScrollAnimation>
              );
            })}
          </div>
        </div>
      </section>

      {/* ────────────────────────── IMPORT ────────────────────────── */}
      <section className="relative mx-[calc(-50vw+50%)] w-screen overflow-hidden px-6 py-16 sm:py-20 lg:px-8">
        <div className="pointer-events-none absolute top-1/2 left-1/2 h-56 w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/[0.05] blur-3xl" />
        <div className="relative z-10 mx-auto max-w-6xl">
          <ScrollAnimation direction="up">
            <SectionHeading title={c.importTitle} />
          </ScrollAnimation>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {c.importSteps.map((step, i) => {
              const Icon = IMPORT_ICONS[i % IMPORT_ICONS.length];
              return (
                <ScrollAnimation key={step} direction="up" delay={i * 70}>
                  <div className="group relative flex h-full flex-col rounded-xl border border-border/40 bg-card/40 p-5 backdrop-blur-sm transition-colors duration-300 hover:border-primary/50">
                    <div className="mb-4 flex items-center justify-between">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-primary/25 bg-primary/[0.08] text-primary transition-transform duration-300 group-hover:scale-110">
                        <Icon className="h-4.5 w-4.5" />
                      </div>
                      <span className="font-mono text-xs font-bold text-primary/50">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <p className="text-sm font-medium leading-relaxed text-foreground">
                      {step}
                    </p>
                  </div>
                </ScrollAnimation>
              );
            })}
          </div>
        </div>
      </section>

      {/* ──────────────────────────── WHY ──────────────────────────── */}
      <section className="relative mx-[calc(-50vw+50%)] w-screen overflow-hidden px-6 py-16 sm:py-24 lg:px-8">
        <HudBackdrop opacity={0.5} />
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute left-1/2 top-1/2 h-64 w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-secondary/[0.07] blur-[110px]" />
        </div>
        <ScrollAnimation direction="up" className="relative z-10 mx-auto max-w-3xl text-center">
          <div className="mb-6 inline-flex items-center gap-2 text-primary">
            <span className="text-2xl">✦</span>
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-4xl">
            {c.whyTitle}
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-muted-foreground">
            {c.whyBody}
          </p>
        </ScrollAnimation>
      </section>

      {/* ─────────────────────────── TOOLS ─────────────────────────── */}
      <section className="relative mx-[calc(-50vw+50%)] w-screen px-6 py-16 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <ScrollAnimation direction="up">
            <SectionHeading title={c.toolsTitle} />
          </ScrollAnimation>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {[
              { text: c.toolsPaper, best: false },
              { text: c.toolsExcel, best: false },
              { text: c.toolsApp, best: true },
            ].map((tool, i) => (
              <ScrollAnimation key={tool.text} direction="up" delay={i * 70}>
                <div
                  className={
                    "group relative flex h-full flex-col overflow-hidden rounded-xl p-6 backdrop-blur-sm transition-all duration-300 " +
                    (tool.best
                      ? "border border-primary/40 bg-primary/[0.06] shadow-lg shadow-primary/10 hover:border-primary/60"
                      : "border border-border/40 bg-card/40 hover:border-border/70")
                  }
                >
                  {tool.best && (
                    <>
                      <div className="pointer-events-none absolute -right-16 -top-16 h-32 w-32 rounded-full bg-primary/20 blur-3xl" />
                      <div className="pointer-events-none absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
                    </>
                  )}
                  <div
                    className={
                      "relative z-10 mb-4 flex h-10 w-10 items-center justify-center rounded-lg border " +
                      (tool.best
                        ? "border-primary/40 bg-primary/15 text-primary"
                        : "border-border/60 bg-muted/40 text-muted-foreground")
                    }
                  >
                    {tool.best ? <Check className="h-5 w-5" /> : <Minus className="h-5 w-5" />}
                  </div>
                  <p
                    className={
                      "relative z-10 text-sm leading-relaxed " +
                      (tool.best ? "font-medium text-foreground" : "text-muted-foreground")
                    }
                  >
                    {tool.text}
                  </p>
                </div>
              </ScrollAnimation>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────── PRICING ────────────────────────── */}
      <section className="relative mx-[calc(-50vw+50%)] w-screen overflow-hidden px-6 py-16 sm:py-20 lg:px-8">
        <div className="pointer-events-none absolute top-0 left-1/2 h-64 w-[700px] -translate-x-1/2 rounded-full bg-primary/[0.06] blur-3xl" />
        <div className="relative z-10 mx-auto max-w-5xl">
          <ScrollAnimation direction="up">
            <SectionHeading title={c.pricingTitle} />
          </ScrollAnimation>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {[
              { text: c.pricingFree, featured: false },
              { text: c.pricingPro, featured: false },
              { text: c.pricingMax, featured: true },
            ].map((plan, i) => {
              // Split "Nom : reste" into a bold plan name + description.
              const idx = plan.text.indexOf(":");
              const name = idx > 0 ? plan.text.slice(0, idx).trim() : "";
              const body = idx > 0 ? plan.text.slice(idx + 1).trim() : plan.text;
              return (
                <ScrollAnimation key={plan.text} direction="up" delay={i * 70}>
                  <div
                    className={
                      "group relative flex h-full flex-col overflow-hidden rounded-xl p-6 backdrop-blur-sm transition-all duration-300 " +
                      (plan.featured
                        ? "border border-primary/45 bg-primary/[0.07] shadow-lg shadow-primary/10"
                        : "border border-border/40 bg-card/50 hover:border-border/80")
                    }
                  >
                    {plan.featured && (
                      <>
                        <div className="pointer-events-none absolute -right-16 -top-16 h-32 w-32 rounded-full bg-primary/20 blur-3xl" />
                        <div className="pointer-events-none absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
                      </>
                    )}
                    {name && (
                      <span
                        className={
                          "relative z-10 font-display text-lg font-bold " +
                          (plan.featured ? "text-primary" : "text-foreground")
                        }
                      >
                        {name}
                      </span>
                    )}
                    <p className="relative z-10 mt-2 text-sm leading-relaxed text-muted-foreground">
                      {body}
                    </p>
                  </div>
                </ScrollAnimation>
              );
            })}
          </div>
        </div>
      </section>

      {/* ──────────────────────────── FAQ ──────────────────────────── */}
      <section className="relative mx-[calc(-50vw+50%)] w-screen px-6 py-16 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <ScrollAnimation direction="up">
            <SectionHeading title={c.faqTitle} />
          </ScrollAnimation>

          <dl className="space-y-3">
            {c.faq.map((f, i) => (
              <ScrollAnimation key={f.q} direction="up" delay={i * 40}>
                <div className="group rounded-xl border border-border/40 bg-card/40 p-5 backdrop-blur-sm transition-colors duration-300 hover:border-primary/40">
                  <dt className="flex items-start gap-3 font-semibold text-foreground">
                    <span className="mt-0.5 select-none font-mono text-sm text-primary">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{f.q}</span>
                  </dt>
                  <dd className="mt-2 pl-7 text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </dd>
                </div>
              </ScrollAnimation>
            ))}
          </dl>
        </div>
      </section>

      {/* ─────────────────────── FINAL CTA ─────────────────────── */}
      <section className="relative mx-[calc(-50vw+50%)] w-screen overflow-hidden px-6 py-20 sm:py-24 lg:px-8">
        <div className="pointer-events-none absolute inset-0 bg-background-subtle" />
        <HudBackdrop opacity={0.4} />
        <div className="pointer-events-none absolute top-1/2 left-1/2 h-64 w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/[0.08] blur-3xl" />
        {/* HUD corner decorations */}
        <div className="pointer-events-none absolute left-8 top-8 h-8 w-8 rounded-tl-sm border-l-2 border-t-2 border-primary/30" />
        <div className="pointer-events-none absolute right-8 top-8 h-8 w-8 rounded-tr-sm border-r-2 border-t-2 border-primary/30" />
        <div className="pointer-events-none absolute bottom-8 left-8 h-8 w-8 rounded-bl-sm border-b-2 border-l-2 border-primary/30" />
        <div className="pointer-events-none absolute bottom-8 right-8 h-8 w-8 rounded-br-sm border-b-2 border-r-2 border-primary/30" />

        <ScrollAnimation direction="up" className="relative z-10 mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            {c.ctaTitle}
          </h2>
          <div className="mt-8 flex justify-center">
            <BeamButton size="lg" showArrow className="px-8 text-base">
              {c.ctaButton}
            </BeamButton>
          </div>
        </ScrollAnimation>
      </section>
    </MarketingPageLayout>
  );
}

// Reusable eyebrow + centered heading with a teal tick, matching the marketing
// section rhythm.
function SectionHeading({ title }: { title: string }) {
  return (
    <div className="mb-10 text-center sm:mb-12">
      <h2 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl md:text-4xl">
        {title}
      </h2>
      <div className="mx-auto mt-4 h-0.5 w-12 rounded-full bg-gradient-to-r from-transparent via-primary to-transparent" />
    </div>
  );
}
